'use client';
import React, { useMemo, useEffect, useState } from 'react';
import { CATEGORIES } from '@/lib/worksheetData';
import { useWorksheetStore } from './worksheetStore';
import { worksheetService, WorksheetRow } from '@/lib/supabase/worksheetService';
import { Worksheet } from '@/lib/worksheetData';
import WorksheetCard from './WorksheetCard';
import CategoryFilterBar from './CategoryFilterBar';
import GradeFilterBar from './GradeFilterBar';
import WorksheetSortControl from './WorksheetSortControl';
import WorksheetEmptyState from './WorksheetEmptyState';
import { Loader2 } from 'lucide-react';

function rowToWorksheet(row: WorksheetRow): Worksheet & { file_path: string } {
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    grade: row.grade,
    pages: row.pages,
    downloads: row.downloads,
    status: row.status,
    uploadedAt: new Date(row.uploaded_at).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }),
    description: row.description,
    fileSize: row.file_size,
    file_path: row.file_path,
  };
}

export default function WorksheetGrid() {
  const { searchQuery, selectedCategory, selectedGrade, sortBy } = useWorksheetStore();
  const [allWorksheets, setAllWorksheets] = useState<(Worksheet & { file_path: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    worksheetService.getPublished({
      category: selectedCategory,
      grade: selectedGrade,
      search: searchQuery,
      sortBy,
    }).then((rows) => {
      if (!cancelled) {
        setAllWorksheets(rows.map(rowToWorksheet));
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(err?.message || 'Failed to load worksheets');
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [searchQuery, selectedCategory, selectedGrade, sortBy]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allWorksheets.forEach((w) => {
      counts[w.category] = (counts[w.category] || 0) + 1;
    });
    return counts;
  }, [allWorksheets]);

  return (
    <section className="flex-1">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
        {/* Category Stats Row */}
        <div className="flex flex-wrap gap-3 mb-6">
          {CATEGORIES?.map((cat) => (
            <div
              key={cat?.id}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${cat?.color} ${cat?.textColor}`}
            >
              {cat?.name}
              <span className="font-bold">{categoryCounts[cat.name] ?? cat.count}</span>
            </div>
          ))}
        </div>

        {/* Category Filter + Sort Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-3">
          <CategoryFilterBar />
          <WorksheetSortControl />
        </div>

        {/* Grade Filter Row */}
        <div className="mb-6">
          <GradeFilterBar />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader2 size={22} className="animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading worksheets…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <p className="text-sm font-semibold text-foreground">Could not load worksheets</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        ) : (
          <>
            {/* Results count */}
            <p className="text-sm text-muted-foreground mb-5">
              Showing{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {allWorksheets?.length}
              </span>{' '}
              worksheet{allWorksheets?.length !== 1 ? 's' : ''}
              {selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}
              {selectedGrade !== 'All' ? ` · ${selectedGrade}` : ''}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>

            {allWorksheets?.length === 0 ? (
              <WorksheetEmptyState searchQuery={searchQuery} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
                {allWorksheets?.map((ws) => (
                  <WorksheetCard key={ws?.id} worksheet={ws} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}